import { Avatar } from '@amore/ui/components/avatar'
import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { Card, CardHeader, CardTitle } from '@amore/ui/components/card'
import { Text } from '@amore/ui/components/typography'
import { Camera, Clock3, PencilLine, Plus, Trash2 } from 'lucide-react'
import { initials } from '@/lib/format'
import { supplierStatusLabels, supplierStatusTone } from '@/lib/labels'
import { type CrewWithSupplier, photoRoleLabels } from '@/mock/photo'
import { useCanEdit } from '@/mock/store'
import type { PhotoRole } from '@/mock/types'

const roleOrder = Object.keys(photoRoleLabels) as PhotoRole[]

const emptyCopy: Partial<Record<PhotoRole, { hint: string; pick: string }>> = {
  photo: {
    hint: 'Dobří fotografové mívají termíny obsazené rok dopředu.',
    pick: 'Vybrat fotografa',
  },
  video: {
    hint: 'Kameramana je dobré poptat zároveň s fotografem.',
    pick: 'Vybrat kameramana',
  },
}

/** "Kdo fotí a natáčí": everybody photographing or filming, each with their hours. */
export function PhotoCrew({
  crew,
  onOpen,
  onAdd,
  onRemove,
}: {
  crew: CrewWithSupplier[]
  onOpen: (member: CrewWithSupplier) => void
  onAdd: (role: PhotoRole) => void
  onRemove: (member: CrewWithSupplier) => void
}) {
  const canEdit = useCanEdit()
  const missing = roleOrder.filter((role) => !crew.some((member) => member.role === role))
  const rows = roleOrder.flatMap((role) => crew.filter((member) => member.role === role))
  return (
    <Card padding="none" className="overflow-hidden">
      <CardHeader appearance="strip">
        <div className="flex items-center gap-2.5">
          <Camera className="size-4 text-action" aria-hidden="true" />
          <CardTitle as="h2">Kdo fotí a natáčí</CardTitle>
        </div>
      </CardHeader>
      <div className="divide-y divide-border px-5">
        {rows.map((row) => (
          <div key={row.id} className="flex items-center gap-3 py-4">
            {row.supplier ? (
              <>
                <Avatar alt="" fallback={initials(row.supplier.name)} />
                <div className="min-w-0 flex-1 space-y-1">
                  <Text variant="small" tone="muted">
                    {photoRoleLabels[row.role]}
                  </Text>
                  <button
                    type="button"
                    onClick={() => onOpen(row)}
                    className="block text-left text-sm font-semibold text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                  >
                    {row.supplier.name}
                  </button>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={supplierStatusTone(row.supplier.status)}>
                      {supplierStatusLabels[row.supplier.status]}
                    </Badge>
                    <Text variant="small" tone="muted" className="inline-flex items-center gap-1">
                      <Clock3 className="size-3.5" aria-hidden="true" />
                      {row.arrival && row.end
                        ? `Na svatbě ${row.arrival}–${row.end}`
                        : row.arrival
                          ? `Na svatbě od ${row.arrival}`
                          : 'Doplňte, od kdy do kdy bude na svatbě'}
                    </Text>
                  </div>
                </div>
              </>
            ) : (
              <EmptySlot role={row.role} canEdit={canEdit} onPick={() => onOpen(row)} />
            )}
            {canEdit ? (
              <div className="flex shrink-0 items-center">
                <Button
                  appearance="ghost"
                  tone="neutral"
                  size="icon"
                  aria-label={`Upravit: ${row.supplier?.name ?? photoRoleLabels[row.role]}`}
                  onClick={() => onOpen(row)}
                >
                  <PencilLine className="size-4" aria-hidden="true" />
                </Button>
                <Button
                  appearance="ghost"
                  tone="neutral"
                  size="icon"
                  aria-label={`Odebrat: ${row.supplier?.name ?? photoRoleLabels[row.role]}`}
                  onClick={() => onRemove(row)}
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                </Button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {canEdit && missing.length ? (
        <div className="flex flex-wrap items-center gap-1 border-t border-border px-5 py-3">
          <Text variant="small" tone="muted" className="mr-1">
            Přidat
          </Text>
          {missing.map((role) => (
            <Button
              key={role}
              appearance="ghost"
              tone="neutral"
              size="sm"
              onClick={() => onAdd(role)}
            >
              <Plus className="size-3.5" aria-hidden="true" />
              {photoRoleLabels[role]}
            </Button>
          ))}
        </div>
      ) : null}
    </Card>
  )
}

function EmptySlot({
  role,
  canEdit,
  onPick,
}: {
  role: PhotoRole
  canEdit: boolean
  onPick: () => void
}) {
  const copy = emptyCopy[role]
  return (
    <>
      <Avatar alt="" fallback="?" />
      <div className="min-w-0 flex-1 space-y-1">
        <Text variant="small" tone="muted">
          {photoRoleLabels[role]}
        </Text>
        <Text>Zatím nevybraný</Text>
        {canEdit ? (
          <>
            {copy ? (
              <Text variant="small" tone="muted">
                {copy.hint}
              </Text>
            ) : null}
            <div className="pt-1">
              <Button appearance="outline" tone="neutral" size="sm" onClick={onPick}>
                {copy?.pick ?? 'Vybrat dodavatele'}
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </>
  )
}
